import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import {
  Loader2, Sparkles, Eye, ArrowLeft, CheckCircle2, X, Monitor, Smartphone, Tablet,
  LayoutTemplate,
} from 'lucide-react';
import { websiteAPI } from '../../services/api';
import { useTenantBranding } from '../../context/TenantBrandingContext';

const DEVICE_CLASS = {
  desktop: 'w-full',
  tablet: 'w-[820px] max-w-full mx-auto',
  mobile: 'w-[400px] max-w-full mx-auto',
};

/**
 * First step of the website onboarding journey — lists every available
 * template, shows a live iframe preview, and lets the user pick one.
 *
 * Currently ships with a single `white-label` template but the grid + preview
 * modal are built for many cards. Adding templates only requires seeding the
 * DB (no UI work here).
 */
const TemplateGallery = () => {
  const navigate = useNavigate();
  const { tenant, fetchTenant } = useTenantBranding();

  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selecting, setSelecting] = useState(null);

  const [previewOf, setPreviewOf] = useState(null);
  const [device, setDevice] = useState('desktop');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await websiteAPI.listTemplates();
      const list = resp.data?.data?.templates || [];
      setTemplates(list);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'تعذّر تحميل القوالب');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const currentTemplateId = tenant?.publicSite?.templateId
    ? String(tenant.publicSite.templateId)
    : null;

  const onChoose = async (template) => {
    setSelecting(template.slug);
    try {
      await websiteAPI.selectTemplate(template.slug);
      // Refresh the tenant in context so WebsiteRouter sees the new
      // `publicSite.templateId` and routes to the onboarding wizard
      // instead of re-rendering the gallery. Without this the user sees
      // the success toast but nothing changes until a manual refresh.
      await fetchTenant();
      toast.success('تم اختيار القالب');
      setPreviewOf(null);
      navigate('/settings/website', { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.message || 'تعذّر اختيار القالب');
    } finally {
      setSelecting(null);
    }
  };

  const previewUrl = useMemo(() => {
    if (!previewOf) return '';
    const host = previewOf.previewHost || `demo-${previewOf.slug}.sites.listechs.com`;
    return `https://${host}/?_demo=1`;
  }, [previewOf]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" dir="rtl">
        <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]" dir="rtl">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8">
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-600 to-blue-700 flex items-center justify-center shadow-sm shrink-0">
              <LayoutTemplate className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-extrabold text-slate-900 mb-1">اختر قالب موقعك</h1>
              <p className="text-slate-500 text-sm max-w-xl">
                اختر من بين قوالبنا الجاهزة. كل قالب مبني بعناية للمختبرات الطبية — عاين قبل الاختيار، وبعدها اتبع خطوات سريعة لإعداد موقعك.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Grid */}
        {templates.length === 0 ? (
          <div className="text-center py-20">
            <Sparkles className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-500">لا توجد قوالب متاحة حالياً.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {templates.map((t) => {
              const isCurrent = currentTemplateId === t._id;
              return (
                <motion.div
                  key={t._id}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden flex flex-col hover:shadow-md transition-shadow"
                >
                  {/* Thumbnail — if previewImageUrl is empty, show a gradient placeholder
                      with the template name. The live iframe preview (modal) is the
                      primary way users compare templates. */}
                  <div className="relative aspect-[16/10] bg-gradient-to-br from-blue-50 via-sky-50 to-blue-100 border-b border-slate-100">
                    {t.previewImageUrl ? (
                      <img
                        src={t.previewImageUrl}
                        alt={t.name?.ar || t.slug}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="text-center px-6">
                          <LayoutTemplate className="w-10 h-10 text-blue-500/60 mx-auto mb-2" />
                          <p className="text-slate-600 font-bold text-lg">{t.name?.ar}</p>
                          <p className="text-slate-400 text-xs mt-1">{t.name?.en}</p>
                        </div>
                      </div>
                    )}
                    {isCurrent && (
                      <span className="absolute top-3 start-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/95 backdrop-blur text-blue-700 text-xs font-semibold shadow">
                        <CheckCircle2 className="w-3.5 h-3.5" />
                        مُستخدم حالياً
                      </span>
                    )}
                  </div>

                  <div className="p-5 flex flex-col flex-1">
                    <h3 className="text-base font-bold text-slate-900 mb-1">{t.name?.ar}</h3>
                    <p className="text-xs text-slate-500 leading-relaxed mb-4 line-clamp-3">
                      {t.description?.ar}
                    </p>
                    <div className="mt-auto grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        onClick={() => { setPreviewOf(t); setDevice('desktop'); }}
                        className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl border border-slate-200 text-slate-700 text-sm font-semibold hover:bg-slate-50 transition-colors"
                      >
                        <Eye className="w-4 h-4" />
                        معاينة
                      </button>
                      <button
                        type="button"
                        onClick={() => onChoose(t)}
                        disabled={selecting === t.slug}
                        className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-60 transition-colors"
                      >
                        {selecting === t.slug ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            {isCurrent ? 'إعادة اختيار' : 'اختر هذا القالب'}
                            <ArrowLeft className="w-4 h-4" />
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>

      {/* Preview Modal */}
      {previewOf && (
        <div
          className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setPreviewOf(null)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-[1400px] h-[85vh] flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
            dir="rtl"
          >
            {/* Modal header */}
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
              <div className="flex items-center gap-3">
                <LayoutTemplate className="w-5 h-5 text-blue-600" />
                <h2 className="text-sm font-bold text-slate-900">
                  معاينة — {previewOf.name?.ar}
                </h2>
              </div>
              <div className="flex items-center gap-2">
                <div className="hidden md:inline-flex items-center gap-1 rounded-xl border border-slate-200 p-1">
                  {[
                    { k: 'desktop', icon: Monitor, label: 'سطح المكتب' },
                    { k: 'tablet',  icon: Tablet,  label: 'تابلت' },
                    { k: 'mobile',  icon: Smartphone, label: 'جوال' },
                  ].map((d) => (
                    <button
                      key={d.k}
                      type="button"
                      onClick={() => setDevice(d.k)}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                        device === d.k
                          ? 'bg-blue-600 text-white'
                          : 'text-slate-600 hover:bg-slate-50'
                      }`}
                    >
                      <d.icon className="w-3.5 h-3.5" />
                      {d.label}
                    </button>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={() => onChoose(previewOf)}
                  disabled={selecting === previewOf.slug}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-60"
                >
                  {selecting === previewOf.slug ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                    <>اختر هذا القالب <ArrowLeft className="w-4 h-4" /></>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => setPreviewOf(null)}
                  className="p-2 rounded-xl text-slate-500 hover:bg-slate-100"
                  aria-label="إغلاق"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Iframe */}
            <div className="flex-1 bg-slate-100 p-4 overflow-auto">
              <div className={`bg-white rounded-xl shadow-md overflow-hidden h-full ${DEVICE_CLASS[device]}`}>
                <iframe
                  key={previewOf.slug + device}
                  src={previewUrl}
                  title={`preview-${previewOf.slug}`}
                  className="w-full h-full border-0"
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TemplateGallery;
